/**
 * WhereViewToggle — three-state segmented control for the WHERE/HAVING card
 * display mode (Slice 3B.2; HAVING parity Plan 3 Task 5). `Auto` defers to the
 * depth/size heuristic; `Inline`/`Rail` pin the view. Read-only display logic;
 * the parent owns the store write + analytics. Mounted in the ClauseCard
 * header `accessory` slot. `kind` (default `'where'`) parameterizes the
 * testid so a HAVING mount never collides with a simultaneously-rendered
 * WHERE toggle — the default keeps WHERE's ids byte-identical. Each segment
 * explains itself in a tooltip (`vqeCopy.whereView`) — "Rail" is otherwise
 * internal vocabulary and "Auto" doesn't say what it picks.
 */

import type { ReactElement } from 'react';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { WhereViewMode } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';

const SEGMENTS: ReadonlyArray<{ readonly mode: WhereViewMode; readonly label: string }> = [
  { mode: 'auto', label: 'Auto' },
  { mode: 'inline', label: 'Inline' },
  { mode: 'rail', label: 'Rail' },
];

const CLAUSE_LABEL: Record<BooleanClauseTarget, string> = {
  where: 'WHERE',
  having: 'HAVING',
};

export function WhereViewToggle({
  value,
  onChange,
  kind = 'where',
}: {
  readonly value: WhereViewMode;
  readonly onChange: (mode: WhereViewMode) => void;
  readonly kind?: BooleanClauseTarget;
}): ReactElement {
  return (
    <span
      className="inline-flex gap-0.5 rounded-md border border-border bg-background p-0.5"
      data-testid={`vqe-${kind}-view-toggle`}
      role="group"
      aria-label={`${CLAUSE_LABEL[kind]} view mode`}
    >
      {SEGMENTS.map(({ mode, label }) => {
        const active = value === mode;
        return (
          <VqeTooltip key={mode} content={vqeCopy.whereView[mode]} selfProvider>
            <button
              type="button"
              data-testid={`vqe-${kind}-view-toggle-${mode}`}
              data-active={active ? 'true' : 'false'}
              aria-pressed={active}
              onClick={() => onChange(mode)}
              className={
                active
                  ? 'rounded px-1.5 py-0.5 text-[11px] font-semibold text-foreground bg-muted'
                  : 'rounded px-1.5 py-0.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground'
              }
            >
              {label}
            </button>
          </VqeTooltip>
        );
      })}
    </span>
  );
}
